import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useStore } from '@/store/useStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
}

const TermsModal = ({ isOpen, onClose, userId }: TermsModalProps) => {
  const acceptTerms = useStore((state) => state.acceptTerms);
  const [isLoading, setIsLoading] = useState(false);
  if (!isOpen) return null;

  const handleAcceptTerms = () => {
    setIsLoading(true);
    acceptTerms();
    onClose();
    setIsLoading(false);
  }


  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !userId && onClose()}>
      <DialogContent className="p-0 max-w-full w-screen h-screen rounded-none overflow-hidden bg-gray-900/70 text-white">
        <DialogHeader className="px-6 pt-6">
          <div className="absolute top-0 left-0 w-full z-20 px-6 py-5 border-b border-gray-700 bg-[#1f2937]">
            <DialogTitle className="text-white text-4xl font-bold text-center mb-2">
              Terms & Conditions
            </DialogTitle>
            <DialogDescription className="text-center text-[#00dcff] text-md">
              Please read these terms carefully before using our service.
            </DialogDescription>
          </div>
        </DialogHeader>
        <div
          className="relative rounded-xl overflow-hidden shadow-2xl"
          style={{ backgroundColor: '#1f2937' }}
        >

          {/* Content */}
          <div className="pt-32 px-6 pb-10 h-full overflow-y-auto text-white text-sm leading-6 space-y-6 mb-4">
            <section>
              <h3 className="text-[#00dcff] text-lg font-semibold mb-2">1. Agreement to Terms</h3>
              <p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">1.1</span>
                    <span>
                      These Terms and Conditions constitute a legally binding agreement made between you, whether personally or on behalf of an entity (you), and Ezyvibes (we, us), concerning your access to and use of Ezyvibes, our service and any related applications (the Site).
                      The Site provides the following services: Subscription-based digital services. You agree that by accessing the Site and/or Services, you have read, understood, and agree to be bound by all of these Terms and Conditions.
                      If you do not agree with all of these Terms and Conditions, then you are prohibited from using the Site and Services and you must discontinue use immediately. We recommend that you print a copy of these Terms and Conditions for future reference</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">1.2</span>
                    <span>
                      The supplemental policies set out in Section 1.7 below, as well as any supplemental terms and conditions or documents that may be posted on the Site from time to time, are expressly incorporated by reference.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">1.3</span>
                    <span>
                      We may make changes to these Terms and Conditions at any time. The updated version of these Terms and Conditions will be indicated by an updated “Revised” date and the updated version will be effective as soon as it is accessible. You are responsible for reviewing these Terms and Conditions to stay informed of updates. Your continued use of the Site represents that you have accepted such changes.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">1.4</span>
                    <span>
                      We may update or change the Site from time to time to reflect changes to our products, our users’ needs and/or our business priorities.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">1.5</span>
                    <span>
                      Our site is directed to people residing in the United Kingdom. The information provided on the Site is not intended for distribution to or use by any person or entity in any jurisdiction or country where such distribution or use would be contrary to law or regulation or which would subject us to any registration requirement within such jurisdiction or country.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">1.6</span>
                    <span>
                      The Site is intended for users who are at least 18 years old. If you are under the age of 18, you are not permitted to register for the Site or use the Services without parental permission.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">1.7</span>
                    <span>
                      Additional policies which also apply to your use of the Site include: Our Privacy Notice, which sets out the terms on which we process any personal data we collect from you, or that you provide to us. By using the Site, you consent to such processing and you warrant that all data provided by you is accurate. Our Cookie Policy provides detailed information about the types of cookies used on the Site.
                    </span>
                  </li>
                </ul>
              </p>
            </section>

            <section>
              <h3 className="text-[#00dcff] text-lg font-semibold mb-2">2. Acceptable Use</h3>
              <p>
                <ul>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">2.1</span>
                    <span>
                      Our Acceptable Use Policy
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">2.2</span>
                    <span>
                      You may not access or use the Site for any purpose other than that for which we make the site and our services available. The Site may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.
                      The service is billed on a subscription basis. You will be billed in advance on a recurring
                      and periodic basis (e.g. monthly). Subscription fees are non-refundable except as required by law.
                    </span>
                  </li>
                </ul>
              </p>
            </section>

            {/* 3. Information you provide to us */}
            <section>
              <h3 className="text-[#00dcff] text-lg font-semibold mb-2">3. Information you provide to us</h3>
              <p className="mb-2">
                <ul>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">3.1</span>
                    <span>
                      You represent and warrant that:
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(a)</span>
                    <span>
                      All registration information you submit will be true, accurate, current, and complete and relate to you and not a third party;
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(b)</span>
                    <span>
                      You will maintain the accuracy of such information and promptly update such information as necessary;
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(c)</span>
                    <span>
                      You will keep your password confidential and will be responsible for all use of your password and account;
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(d)</span>
                    <span>
                      You have the legal capacity and you agree to comply with these Terms and Conditions; and
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(e)</span>
                    <span>
                      You are not a minor in the jurisdiction in which you reside, or if a minor, you have received parental permission to use the Site.
                      If you know or suspect that anyone other than you knows your user information (such as an identification code or user name) and/or password you must promptly notify us at contact@ezyvibes.com
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">3.2</span>
                    <span>
                      If you provide any information that is untrue, inaccurate, not current or incomplete, we may suspend or terminate your account. We may remove or change a user name you select if we determine that such user name is inappropriate.
                    </span>
                  </li>
                </ul>
              </p>
            </section>

            {/* 4. Health & Allergy Disclaimer */}
            <section>
              <h3 className="text-[#00dcff] text-lg font-semibold mb-2">4. Health & Allergy Disclaimer</h3>
              <p className='mb-2'>
                <ul>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">4.1</span>
                    <span>
                      The content provided by Ezyvibes, including but not limited to recipes, meal plans, and shopping lists, is for informational purposes only. It is not intended as medical advice, diagnosis, or treatment.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">4.2</span>
                    <span>
                      Users are solely responsible for checking all ingredients, labels, and allergen warnings when purchasing or preparing food. Ezyvibes does not guarantee the allergen-free status of any recipe or product.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">4.3</span>
                    <span>
                      We disclaim liability for any adverse reactions, allergic responses, or health issues arising from use of our content. If you have dietary restrictions or medical conditions, consult a qualified healthcare professional.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">4.4</span>
                    <span>
                      We disclaim liability for any adverse reactions, allergic responses, or health issues arising from use of our content. If you have dietary restrictions or medical conditions, consult a qualified healthcare professional.
                    </span>
                  </li>
                </ul>
              </p>
              <p>
                See our full{" "}
                <a
                  href="https://ezyvibes.com/disclaimer"
                  className="ml-2 text-secondary underline cursor-pointer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Health & Allergy Disclaimer
                </a>
                .
              </p>
            </section>

            <section>
              <h3 className="text-[#00dcff] text-lg font-semibold mb-2">5. Our Content</h3>
              <p className='mb-2'>
                <ul>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">5.1</span>
                    <span>
                      We upload recipes which are for guidance only. It is the whole responsibility of the user/customer to ensure that they are not consuming something to which they may have an adverse reaction – and Ezyvibes are not liable for the customers’ improper use of our recipes or what they consume. Additionally, we cannot guarantee that all of our recipes are free from allergens or certain ingredients, and it is the responsibility of the consumer to ensure they are not preparing meals that contain or may contain ingredients that are not suitable for consumption or personal consumption.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">5.2</span>
                    <span>
                      Unless otherwise indicated, the Site and Services including source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics on the Site (Our Content) are owned or licensed to us, and are protected by copyright and trade mark laws.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">5.3</span>
                    <span>
                      Except as expressly provided in these Terms and Conditions, no part of the Site, Services or Our Content may be copied, reproduced, aggregated, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise exploited for any commercial purpose whatsoever, without our express prior written permission.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">5.4</span>
                    <span>
                      Provided that you are eligible to use the Site, you are granted a limited license to access and use the Site and Our Content and to download or print a copy of any portion of the Content to which you have properly gained access solely for your personal, non-commercial use.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">5.4</span>
                    <span>
                      You shall not:
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(a)</span>
                    <span>
                      try to gain unauthorized access to the Site or any networks, servers or computer systems connected to the Site; and/or
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(b)</span>
                    <span>
                      make for any purpose including error correction, any modifications, adaptions, additions or enhancements to the Site or Our Content, including the modification of the paper or digital copies you may have downloaded.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">5.5</span>
                    <span>
                      We shall:
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(a)</span>
                    <span>
                      prepare the Site and Our Content with reasonable skill and care; and
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(b)</span>
                    <span>
                      use industry standard virus detection software to try to block the uploading of content to the Site that contains viruses.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">5.6</span>
                    <span>
                      The content on the Site is provided for general information only. It is not intended to amount to advice on which you should rely.
                      You must obtain professional or specialist advice before taking, or refraining from taking, any action on the basis of the content on the Site.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">5.7</span>
                    <span>
                      Although we make reasonable efforts to update the information on our site, we make no representations,
                      warranties or guarantees, whether express or implied, that Our Content on the Site is accurate, complete or up to date.
                    </span>
                  </li>
                </ul>
              </p>
            </section>

            <section>
              <h3 className="text-[#00dcff] text-lg font-semibold mb-2">6. Site Management</h3>
              <p>
                <ul>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">6.1</span>
                    <span>
                      We reserve the right at our sole discretion, to:
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(1)</span>
                    <span>
                      monitor the Site for breaches of these Terms and Conditions;
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(2)</span>
                    <span>
                      take appropriate legal action against anyone in breach of applicable laws or these Terms and Conditions;
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(3)</span>
                    <span>
                      refuse, restrict access to or availability of, or disable (to the extent technologically feasible) any of your Contributions;
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(4)</span>
                    <span>
                      remove from the Site or otherwise disable all files and content that are excessive in size or are in any way a burden to our systems; and
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">(4)</span>
                    <span>
                      otherwise manage the Site in a manner designed to protect our rights and property and to facilitate the proper functioning of the Site and Services.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">6.2</span>
                    <span>
                      We do not guarantee that the Site will be secure or free from bugs or viruses.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">6.3</span>
                    <span>
                      You are responsible for configuring your information technology, computer programs and platform to access the Site and you should use your own virus protection software.
                    </span>
                  </li>
                </ul>
              </p>
            </section>

            <section>
              <h3 className="text-[#00dcff] text-lg font-semibold mb-2">8. Modifications to and availability of the Site</h3>
              <p>
                <ul>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">8.1</span>
                    <span>
                      We reserve the right to change, modify, or remove the contents of the Site at any time or for any reason at our sole discretion without notice. We also reserve the right to modify or discontinue all or part of the Services without notice at any time.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">8.2</span>
                    <span>
                      We cannot guarantee the Site and Services will be available at all times. We may experience hardware, software, or other problems or need to perform maintenance related to the Site, resulting in interruptions, delays, or errors. You agree that we have no
                      liability whatsoever for any loss, damage, or inconvenience caused by your inability to access or use the Site or
                      Services during any downtime or discontinuance of the Site or Services. We are not obliged to maintain and support the Site or Services or to supply any corrections, updates, or releases.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-10 shrink-0">8.3</span>
                    <span>
                      There may be information on the Site that contains typographical errors, inaccuracies, or omissions that may relate to the Services,
                      including descriptions, pricing, availability, and various other information. We reserve the right to correct any errors, inaccuracies, or omissions and to change or update the information at any time, without prior notice.
                    </span>
                  </li>
                </ul>
              </p>
            </section>

            <section>
              <h3 className="text-[#00dcff] text-lg font-semibold mb-2">9. Disclaimer/Limitation of Liability</h3>
              <ul>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">9.1</span>
                  <span>
                    The Site and Services are provided on an as-is and as-available basis. You agree that your use of the Site and/or Services will be at your sole risk except as expressly set out in these Terms and Conditions. All warranties, terms, conditions and undertakings, express or implied (including by statute, custom or usage, a course of dealing, or common law) in connection with the Site and Services and your use thereof including, without limitation, the implied warranties of satisfactory quality, fitness for a particular purpose and non-infringement are excluded to the fullest extent permitted by applicable law. We make no warranties or representations about the accuracy or completeness of the Site’s content and are not liable for any (1) errors or omissions in content; (2) any unauthorized access to or use of our servers and/or any and all personal information and/or financial information stored on our server; (3) any interruption or cessation of transmission to or from the site or services; and/or (4) any bugs, viruses, trojan horses, or the like which may be transmitted to or through the site by any third party. We will not be responsible for any delay or failure to comply with our obligations under these Terms and Conditions if such delay or failure is caused by an event beyond our reasonable control.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">9.2</span>
                  <span>
                    Our responsibility for loss or damage suffered by you: Whether you are a consumer or a business user:
                  </span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-[#00dcff] text-lg font-semibold mb-2">10. Term and Termination</h3>
              <ul>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">10.1</span>
                  <span>
                    These Terms and Conditions shall remain in full force and effect while you use the Site or Services or are otherwise a user of the Site, as applicable. You may terminate your use or participation at any time, for any reason, by following the instructions for terminating user accounts in your account settings, if available, or by contacting us at contact@ezyvibes.com
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">10.2</span>
                  <span>
                    Without limiting any other provision of these Terms and Conditions, we reserve the right to, in our sole discretion and without notice or liability, deny access to and use of the Site and the Services (including blocking certain IP addresses), to any person for any reason including without limitation for breach of any representation, warranty or covenant contained in these Terms and Conditions or of any applicable law or regulation.

                    If we determine, in our sole discretion, that your use of the Site/Services is in breach of these Terms and Conditions or of any applicable law or regulation, we may terminate your use or participation in the Site and the Services or delete your profile and any content or information that you posted at any time, without warning, in our sole discretion.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">10.3</span>
                  <span>
                    If we terminate or suspend your account for any reason set out in this Section 10, you are prohibited from registering and creating a new account under your name, a fake or borrowed name, or the name of any third party, even if you may be acting on behalf of the third party. In addition to terminating or suspending your account, we reserve the right to take appropriate legal action, including without limitation pursuing civil, criminal, and injunctive redress.
                  </span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-[#00dcff] text-lg font-semibold mb-2">11. General</h3>
              <ul>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">11.1</span>
                  <span>
                    Visiting the Site, sending us emails, and completing online forms constitute electronic communications. You consent to receive electronic communications and you agree that all agreements, notices, disclosures, and other communications we provide to you electronically, via email and on the Site, satisfy any legal requirement that such communication be in writing. You hereby agree to the use of electronic signatures, contracts, orders and other records and to electronic delivery of notices, policies and records of transactions initiated or completed by us or via the Site. You hereby waive any rights or requirements under any statutes, regulations, rules, ordinances or other laws in any jurisdiction which require an original signature or delivery or retention of non-electronic records, or to payments or the granting of credits by other than electronic means.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">11.2</span>
                  <span>
                    These Terms and Conditions and any policies or operating rules posted by us on the Site or in respect to the Services constitute the entire agreement and understanding between you and us.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">11.3</span>
                  <span>
                    Our failure to exercise or enforce any right or provision of these Terms and Conditions shall not operate as a waiver of such right or provision.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">11.4</span>
                  <span>
                    We may assign any or all of our rights and obligations to others at any time.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">11.5</span>
                  <span>
                    We shall not be responsible or liable for any loss, damage, delay or failure to act caused by any cause beyond our reasonable control.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">11.6</span>
                  <span>
                    If any provision or part of a provision of these Terms and Conditions is unlawful, void or unenforceable, that provision or part of the provision is deemed severable from these Terms and Conditions and does not affect the validity and enforceability of any remaining provisions.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">11.7</span>
                  <span>
                    There is no joint venture, partnership, employment or agency relationship created between you and us as a result of these Terms and Conditions or use of the Site or Services.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">11.8</span>
                  <span>
                    A person who is not a party to these Terms and Conditions shall have no right under the Contracts (Rights of Third Parties) Act 1999 to enforce any term of these Terms and Conditions.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-10 shrink-0">11.9</span>
                  <span>
                    In order to resolve a complaint regarding the Services or to receive further information regarding use of the Services, please contact us by email at contact@ezyvibes.com
                  </span>
                </li>
              </ul>
            </section>

            {userId && <section className='item-center mb-2'>
              <button className="text-black bg-secondary px-3 py-2 rounded-md hover:bg-secondary/90" onClick={() => handleAcceptTerms()}>
                {isLoading? "Please wait..." : "I accept the terms and conditions"}
              </button>
            </section>}
          </div>

          {/* Footer */}
          <p className="bg-[#1f2937] absolute bottom-0 p-2 w-full text-center text-sm text-gray-400">
            By continuing, you acknowledge that you have read and agree to our Terms and Privacy Policy.
          </p>
        </div>
      </DialogContent >
      </Dialog>
      );
};

      export default TermsModal;